import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { PlayCircle, Calendar, Clock, Brain, ArrowLeft, UserSquare2, ClipboardList } from 'lucide-react';
import Sidebar from '@/components/Sidebar';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

interface PracticeInterview {
  id: string;
  role: string;
  level: string;
  skills: string[];
  created_at: string;
  completed_at: string | null;
  transcript: Array<{ source: string; message: string }> | null;
}

const PracticeHistory: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [interviews, setInterviews] = useState<PracticeInterview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInterview, setSelectedInterview] = useState<PracticeInterview | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    fetchPracticeInterviews();
  }, [user, navigate]);

  const fetchPracticeInterviews = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('practice_interviews')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setInterviews(data || []);
    } catch (error) {
      console.error('Error fetching practice interviews:', error);
      toast.error('Could not load practice history');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewTranscript = (interview: PracticeInterview) => {
    setSelectedInterview(interview);
    setShowTranscript(true);
  };

  const handleStartNewPractice = () => {
    navigate('/practice');
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Format time helper
  const formatTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'h:mm a');
    } catch (error) {
      return 'Invalid time';
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return 'Unknown time';
    }
  };

  // Get interview status
  const getInterviewStatus = (interview: PracticeInterview) => {
    if (interview.completed_at) {
      return 'completed';
    } else {
      return 'in-progress';
    }
  };

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex-1 overflow-auto bg-gray-50">
        <div className="container p-6 mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Practice Interview History</h1>
              <p className="text-gray-600 mt-1">
                Review all your practice interviews and track your progress
              </p>
            </div>
            <Button 
              onClick={handleStartNewPractice}
              className="bg-gradient-to-r from-[#9b87f5] to-[#7662DA] hover:from-[#8A76E5] hover:to-[#6552CA]"
            >
              <PlayCircle className="mr-2" size={16} />
              Start New Practice
            </Button>
          </div>

          <Card className="shadow-sm">
            <CardHeader className="bg-white pb-4">
              <CardTitle>Your Practice Sessions</CardTitle>
              <CardDescription>
                You've completed {interviews.filter(i => i.completed_at).length} practice interviews
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="p-4 space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex items-center space-x-4">
                      <Skeleton className="h-12 w-12 rounded-full" />
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-[250px]" />
                        <Skeleton className="h-4 w-[200px]" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : interviews.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Role</TableHead>
                      <TableHead>Level</TableHead>
                      <TableHead>Skills</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {interviews.map((interview) => (
                      <TableRow key={interview.id}>
                        <TableCell className="font-medium">{interview.role}</TableCell>
                        <TableCell>
                          {interview.level.charAt(0).toUpperCase() + interview.level.slice(1)}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {interview.skills.slice(0, 3).map((skill, i) => (
                              <Badge key={i} variant="outline" className="bg-[#F8F7FF] border-[#9b87f5]/30">
                                {skill}
                              </Badge>
                            ))}
                            {interview.skills.length > 3 && (
                              <Badge variant="outline" className="bg-gray-100 border-gray-300">
                                +{interview.skills.length - 3}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-xs flex items-center">
                              <Calendar className="mr-1" size={12} />
                              {formatDate(interview.created_at)}
                            </span>
                            <span className="text-xs text-gray-500 flex items-center">
                              <Clock className="mr-1" size={12} />
                              {formatTime(interview.created_at)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getInterviewStatus(interview) === 'completed' ? (
                            <Badge className="bg-green-100 text-green-800 border-green-200">
                              Completed
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                              In Progress
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewTranscript(interview)}
                            disabled={!interview.transcript}
                            className="text-[#9b87f5] border-[#9b87f5]/30 hover:bg-[#9b87f5]/10"
                          >
                            <ClipboardList className="mr-1" size={14} />
                            View Transcript
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="flex flex-col items-center justify-center p-12 text-center">
                  <div className="w-16 h-16 bg-[#F8F7FF] rounded-full flex items-center justify-center mb-4">
                    <Brain className="text-[#9b87f5]" size={24} />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No practice interviews yet</h3>
                  <p className="text-gray-500 mb-6 max-w-md">
                    Start practicing interviews to prepare for your job applications. The more you practice, the better you'll get!
                  </p>
                  <Button
                    onClick={handleStartNewPractice}
                    className="bg-gradient-to-r from-[#9b87f5] to-[#7662DA] hover:from-[#8A76E5] hover:to-[#6552CA]"
                  >
                    <PlayCircle className="mr-2" size={16} />
                    Start Your First Practice
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Transcript Sheet */}
      <Sheet open={showTranscript} onOpenChange={setShowTranscript}>
        <SheetContent className="w-full sm:max-w-lg md:max-w-xl overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setShowTranscript(false)}
              >
                <ArrowLeft size={18} />
              </Button>
              Interview Transcript
            </SheetTitle>
            {selectedInterview && (
              <SheetDescription>
                {selectedInterview.role} ({selectedInterview.level}) - {formatDate(selectedInterview.created_at)}
              </SheetDescription>
            )}
          </SheetHeader>

          {selectedInterview && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-[#F8F7FF] p-4 rounded-lg border border-[#9b87f5]/20">
                  <div className="flex items-center gap-2 mb-2">
                    <UserSquare2 size={16} className="text-[#9b87f5]" />
                    <span className="font-medium">Role</span>
                  </div>
                  <p>{selectedInterview.role}</p>
                </div>
                <div className="bg-[#F8F7FF] p-4 rounded-lg border border-[#9b87f5]/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Brain size={16} className="text-[#9b87f5]" />
                    <span className="font-medium">Skills</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {selectedInterview.skills.map((skill, i) => (
                      <Badge key={i} variant="outline" className="bg-white border-[#9b87f5]/30">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="p-3 border-b border-gray-200 bg-gray-50">
                  <h3 className="font-semibold">Conversation</h3>
                </div>
                <div className="p-4 space-y-4 max-h-[500px] overflow-y-auto">
                  {selectedInterview.transcript ? (
                    selectedInterview.transcript.map((msg, i) => (
                      <div key={i} className="mb-4">
                        <p className="font-medium text-sm text-gray-500 mb-1">
                          {msg.source === 'ai' ? 'AI Interviewer' : 'You'}:
                        </p>
                        <p className="text-gray-900">{msg.message}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center">
                      Transcript not available for this practice session.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-center mt-6">
                <Button
                  onClick={handleStartNewPractice}
                  className="bg-gradient-to-r from-[#9b87f5] to-[#7662DA] hover:from-[#8A76E5] hover:to-[#6552CA]"
                >
                  <PlayCircle className="mr-2" size={16} />
                  Start New Practice
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default PracticeHistory; 